import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin, Clock } from "lucide-react";
import { api, formatDate, formatTime } from "@/lib/api";
import { useSeo, breadcrumb, eventJsonLd } from "@/lib/seo";
import { eventType, eventPath } from "@/data/eventTypes";
import { Reveal } from "@/components/Reveal";
import { EventSidebar } from "@/components/EventSidebar";
import { EventSections } from "@/components/EventSections";
import { EventShare } from "@/components/EventShare";
import { BookingDialog } from "@/components/BookingDialog";
import { EventCta } from "@/components/EventCta";
import { circleTypeLabel, isPaganCircle, paganCircleBadge, paganCircleTitle } from "@/lib/cerclesPaiens";
import { useLocale } from "@/context/LocaleContext";
import { isNorwayCare, SoinNorvegeSections } from "@/components/SoinNorvegeSections";

const Squelette = () => (
  <div data-testid="event-loading">
    <div className="h-[60vh] bg-earth animate-pulse" />
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-12"><div className="lg:col-span-7 space-y-4">{[0, 1, 2].map((i) => <div key={i} className="h-5 rounded bg-sand-dark/60 animate-pulse" />)}</div><div className="lg:col-span-5 h-72 rounded-2xl bg-sand-dark/60 animate-pulse" /></div>
  </div>
);

const Introuvable = () => (
  <section className="max-w-3xl mx-auto px-6 pt-44 pb-32 text-center" data-testid="event-not-found">
    <p className="eyebrow mb-6">Événement introuvable</p>
    <h1 className="font-serif text-4xl text-bark leading-tight">Cette date n'existe plus ou a changé d'adresse.</h1>
    <p className="text-fumee mt-6">Retrouvez tous les rendez-vous à venir dans l'agenda.</p>
    <Link to="/agenda" className="mt-10 inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors duration-300" data-testid="event-not-found-agenda">Voir l'agenda</Link>
  </section>
);

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [etat, setEtat] = useState("chargement");
  const [booking, setBooking] = useState(null);
  const { locale } = useLocale();

  useEffect(() => {
    setEtat("chargement");
    api.eventBySlug(slug).then((d) => {
      if (d.redirect_slug) return navigate(`/evenements/${d.redirect_slug}`, { replace: true });
      setEvent(d); setEtat("ok");
    }).catch(() => setEtat("absent"));
  }, [slug, navigate]);

  const config = eventType(event?.type);
  const image = event?.image || config.image;
  const pagan = isPaganCircle(event);
  const norwayCare = isNorwayCare(event);
  const displayedTitle = paganCircleTitle(event, locale);
  const displayedLabel = event?.type === "cercle" ? circleTypeLabel(event, locale) : config.label;
  const displayedConfig = norwayCare ? { ...config, cta: locale === "en" ? "I JOIN THE CARE" : "JE PARTICIPE AU SOIN" } : config;
  useSeo(event ? {
    title: norwayCare ? (locale === "en" ? "Energy care from Norway | Entre Les Mondes" : "Soin énergétique depuis la Norvège | Entre Les Mondes") : `${displayedTitle} — ${event.date_label || formatDate(event.date, { weekday: undefined })} · Entre Les Mondes`,
    description: norwayCare ? (locale === "en" ? "Take part in a collective energy care from Norway on 11 November 2026. Grounding, recentering and connection with Nordic lands." : "Participez à un soin énergétique collectif à distance depuis la Norvège le 11 novembre 2026. Ancrage, recentrage et connexion aux terres nordiques.") : (event.presentation || event.description || "").slice(0, 155),
    image, type: "article", canonicalPath: eventPath(event),
    jsonLd: [breadcrumb([["Accueil", "/"], ["Agenda", "/agenda"], [displayedTitle, eventPath(event)]]), { ...eventJsonLd({ ...event, titre: displayedTitle }), url: absolute(eventPath(event)) }],
  } : { title: null });

  if (etat === "chargement") return <Squelette />;
  if (etat === "absent") return <Introuvable />;
  const horaire = event.date_fin && event.date_fin.slice(0, 10) === event.date.slice(0, 10) ? `${formatTime(event.date)} – ${formatTime(event.date_fin)}` : formatTime(event.date);
  const afficherActionHero = event.type !== "salon" || !!event.lien_externe;

  return (
    <article data-testid="event-detail" data-slug={event.slug}>
      <section className="relative min-h-[70vh] flex items-end bg-earth text-sand overflow-hidden">
        <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" data-testid="event-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-earth via-earth/60 to-earth/10" />
        <div className="relative z-[2] max-w-7xl mx-auto px-6 lg:px-10 pt-44 pb-16 lg:pb-20 w-full">
          <Reveal>
            <Link to={config.retour.to} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-sand/70 hover:text-terra transition-colors mb-8" data-testid="event-back"><ArrowLeft className="w-3.5 h-3.5" /> {config.retour.label}</Link>
            {event.passe && (
              <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-sand/10 border border-sand/25 px-4 py-2 text-xs text-sand/85" data-testid="event-passe-banner">
                <span className="font-mono uppercase tracking-[0.25em] text-terra">Événement passé</span>
                <Link to="/agenda" className="link-line hover:text-terra">Voir les prochaines dates →</Link>
              </div>
            )}
            <p className="eyebrow text-sand/70 mb-5" data-testid="event-editorial-label">{norwayCare ? (locale === "en" ? "ENERGY CARE · FROM NORWAY" : "SOIN ÉNERGÉTIQUE · DEPUIS LA NORVÈGE") : pagan ? paganCircleBadge(event, locale) : `${config.eyebrow} · ${displayedLabel}`}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl" data-testid="event-title">{displayedTitle}</h1>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-sand/85" data-testid="event-hero-meta">
              <span className={`inline-flex items-center gap-2 ${event.date_label ? "" : "capitalize"}`}><CalendarDays className="w-4 h-4 text-terra" />{event.date_label || formatDate(event.date)}</span>
              {!event.date_label && <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-terra" />{horaire}</span>}
              <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-terra" />{event.lieu}</span>
            </div>
            {afficherActionHero && <div className="mt-6 flex w-full sm:w-auto" data-testid="event-hero-booking-action">
              <EventCta event={event} config={displayedConfig} onBook={setBooking} testid="event-hero-cta" tone="dark" className="w-full sm:w-auto px-7" />
            </div>}
            <div className="mt-8"><EventShare event={event} tone="dark" /></div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7 min-w-0">{norwayCare ? <SoinNorvegeSections event={event} /> : <>{pagan && <div className="border-l-2 border-terra pl-5 mb-12" data-testid="event-pagan-context"><p className="eyebrow mb-3">{locale === "en" ? "Living traditions" : "Traditions vivantes"}</p><p className="text-fumee leading-relaxed">{locale === "en" ? "This public ritual gathering links seasons, symbolic practice, intentions and drums. Its contemporary practices are presented as such; documented historical knowledge and modern interpretations are kept distinct." : "Cette rencontre rituelle publique relie saisons, pratique symbolique, intentions et tambours. Les pratiques contemporaines y sont présentées comme telles ; les connaissances historiques documentées et les interprétations modernes sont distinguées avec soin."}</p></div>}<EventSections event={event} config={config} /></>}</div>
        <div className="lg:col-span-5 min-w-0"><EventSidebar event={event} config={displayedConfig} onBook={setBooking} /></div>
      </section>

      <BookingDialog event={booking} open={!!booking} onOpenChange={(v) => !v && setBooking(null)} />
    </article>
  );
}
